import React from 'react'
import { motion } from 'framer-motion'
import { Smartphone, MapPin, Users, Zap, Shield, BarChart3 } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const Services: React.FC = () => {
  const { t } = useLanguage()

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  }

  return (
    <section id="services" className="py-20 bg-white" role="region" aria-label="Services section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            {t('services.title')}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed"
          >
            {t('services.description')}
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16"
        >
          {/* Noozter App */}
          <motion.div
            variants={itemVariants}
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.2 }
            }}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mr-4">
                <Smartphone className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900">{t('services.noozter.title')}</h3>
            </div>
            <ul className="space-y-4">
              <motion.li
                variants={itemVariants}
                className="flex items-center space-x-3"
              >
                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                <span className="text-gray-700 text-lg">Collaborate with fellow workers.</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-center space-x-3"
              >
                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                <span className="text-gray-700 text-lg">Showcase your progress of projects.</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-center space-x-3"
              >
                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                <span className="text-gray-700 text-lg">Experience the world of advancement.</span>
              </motion.li>
            </ul>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-8"
            >
              <a 
                href="https://noozter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 text-center"
              >
                {t('services.learnMore')}
              </a>
            </motion.div>
          </motion.div>

          {/* GeoLocation Services */}
          <motion.div
            variants={itemVariants}
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.2 }
            }}
            className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mr-4">
                <MapPin className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900">{t('services.geolocation.title')}</h3>
            </div>
            <ul className="space-y-4">
              <motion.li
                variants={itemVariants}
                className="flex items-center space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                <span className="text-gray-700 text-lg">Track and share the progress of route</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-center space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                <span className="text-gray-700 text-lg">Integrate in your contact page to protect from spams</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-center space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                <span className="text-gray-700 text-lg">Point and check the location info.</span>
              </motion.li>
            </ul>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-8"
            >
              <button className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-300">
                {t('services.learnMore')}
              </button>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Additional Services Grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          <motion.div
            variants={itemVariants}
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Users className="h-6 w-6 text-white" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">AI Communication</h4>
            <p className="text-gray-600 text-sm">Advanced AI-powered communication solutions</p>
          </motion.div>

          <motion.div
            variants={itemVariants}
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Live Transcription</h4>
            <p className="text-gray-600 text-sm">Real-time speech to text conversion</p>
          </motion.div>

          <motion.div
            variants={itemVariants}
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Secure Storage</h4>
            <p className="text-gray-600 text-sm">Enterprise-grade security and privacy</p>
          </motion.div>

          <motion.div
            variants={itemVariants}
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="h-6 w-6 text-white" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Analytics</h4>
            <p className="text-gray-600 text-sm">Comprehensive reporting and insights</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default Services
